import { Link } from "react-router-dom";
import { useList } from "@/lib/api";
import { PageHeader, StatCard, Spinner, EmptyState } from "@/components/common";
import {
  GraduationCap, ClipboardCheck, MessageSquareText, RefreshCcw, TrendingUp, Award,
  Medal, HeartHandshake, CalendarCheck, Clock, ShieldCheck, Anchor, LifeBuoy, Rocket,
  Trophy, Sparkles, AlertTriangle, Lightbulb, UserX, Plane, Lock, Star, Users,
} from "lucide-react";

export const pct = (r) => (r == null ? "—" : `${Math.round(r * 1000) / 10}%`);

const BADGE_ICONS = {
  "trending-up": TrendingUp, award: Award, medal: Medal, "heart-handshake": HeartHandshake,
  "calendar-check": CalendarCheck, clock: Clock, "clipboard-check": ClipboardCheck,
  "shield-check": ShieldCheck, anchor: Anchor, "life-buoy": LifeBuoy, rocket: Rocket,
};

const CONFIDENCE_STYLES = {
  high: "bg-[#D8F3DC] text-[#2D6A4F]",
  medium: "bg-[#FFF8DC] text-[#B8860B]",
  low: "bg-[#FFEDEC] text-[#D90429]",
  none: "bg-muted text-muted-foreground",
};

export function ConfidenceChip({ value, testId }) {
  return (
    <span data-testid={testId}
      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${CONFIDENCE_STYLES[value] || CONFIDENCE_STYLES.none}`}>
      {value} confidence
    </span>
  );
}

export function ScoreRing({ score, label, sub, testId }) {
  const v = Math.max(0, Math.min(100, Number(score ?? 0)));
  const r = 44, c = 2 * Math.PI * r;
  const color = v >= 80 ? "#2D6A4F" : v >= 60 ? "#B8860B" : "#D90429";
  return (
    <div className="rounded-lg border bg-card p-5 flex items-center gap-5 lift" data-testid={testId}>
      <div className="relative h-[104px] w-[104px] shrink-0">
        <svg width="104" height="104" viewBox="0 0 104 104" className="-rotate-90">
          <circle cx="52" cy="52" r={r} fill="none" stroke="hsl(var(--muted))" strokeWidth="9" />
          <circle cx="52" cy="52" r={r} fill="none" stroke={color} strokeWidth="9"
            strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c - (v / 100) * c}
            style={{ transition: "stroke-dashoffset 0.8s ease" }} />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-2xl font-bold font-display">
          {score == null ? "—" : Math.round(v)}
        </span>
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">{label}</p>
        <p className="text-sm text-muted-foreground mt-1 max-w-[220px]">{sub}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- /teacher/kpi
export default function TeacherKpiPage() {
  const { data: kpi, loading } = useList("/teacher/kpi");
  if (loading) return <Spinner full />;
  if (!kpi) {
    return (
      <div data-testid="teacher-kpi-page">
        <PageHeader title="My KPI" />
        <EmptyState text="KPI visibility is currently disabled by the school." />
      </div>
    );
  }
  const drop = kpi.dropout || {};
  const ops = kpi.operational || {};
  return (
    <div data-testid="teacher-kpi-page">
      <PageHeader title="My KPI"
        description="Your performance over the last 12 months. Scores are based on teaching quality and reliability only — no financial data is involved." />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 stagger-in">
        <ScoreRing score={kpi.kpi_score} label="Overall KPI score" testId="kpi-score-ring"
          sub="Attendance, punctuality, remarks, retention, progression, exams & reliability." />
        <ScoreRing score={ops.score} label="Operational quality" testId="ops-score-ring"
          sub={`Starts at 100 — penalties capped at ${ops.penalty_cap ?? 40}. Replacement classes earn credit back.`} />
        <div className="rounded-lg border bg-card p-5 flex flex-col justify-between lift" data-testid="kpi-rewards-summary">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Reward points</p>
            <Sparkles className="h-4 w-4 text-secondary" />
          </div>
          <p className="text-3xl font-bold font-display mt-2" data-testid="kpi-reward-points">{kpi.reward_points ?? 0} pts</p>
          <p className="text-xs text-muted-foreground mt-1">{(kpi.badges || []).length} badge{(kpi.badges || []).length === 1 ? "" : "s"} earned</p>
          <Link to="/teacher/rewards" className="text-sm text-secondary underline mt-2" data-testid="kpi-view-rewards-link">
            View rewards & badges
          </Link>
        </div>
      </div>

      <h3 className="text-xl font-semibold mt-10 mb-4">Students & progression</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 stagger-in">
        <StatCard icon={Users} label="Students served" value={kpi.total_students_served ?? 0}
          sub={`${kpi.students_retained ?? 0} retained · ${pct(kpi.retention_rate)}`} accent testId="kpi-students-served" />
        <StatCard icon={GraduationCap} label="New students" value={kpi.new_students_assigned ?? 0} testId="kpi-new-students" />
        <StatCard icon={TrendingUp} label="Progressions" value={kpi.students_progressed ?? 0} sub="Approved level-ups" testId="kpi-progressions" />
        <StatCard icon={Award} label="Exam entries" value={kpi.students_entered_exam ?? 0}
          sub={`${kpi.exam_passed ?? 0} passed · ${kpi.exam_passed_distinction ?? 0} distinction`} testId="kpi-exams" />
        <StatCard icon={Star} label="Exam pass rate" value={pct(kpi.exam_pass_rate)} sub="Verified results only" testId="kpi-exam-pass-rate" />
      </div>

      <h3 className="text-xl font-semibold mt-10 mb-4">Teaching quality</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 stagger-in">
        <StatCard icon={ClipboardCheck} label="Attendance rate" value={pct(kpi.attendance_rate)}
          sub={`${kpi.lessons_recorded ?? 0} lessons recorded`} testId="kpi-attendance" />
        <StatCard icon={Clock} label="Punctuality rate" value={pct(kpi.punctuality_rate)} sub="On-time check-ins" testId="kpi-punctuality" />
        <StatCard icon={MessageSquareText} label="Remark completion" value={pct(kpi.lesson_remark_completion_rate)}
          sub="Remarks written per lesson taught" testId="kpi-remarks" />
      </div>

      <h3 className="text-xl font-semibold mt-10 mb-4">Reliability — last 12 months</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 stagger-in">
        <StatCard icon={Plane} label="Leaves" value={kpi.teacher_leave_count ?? 0} testId="kpi-leaves" />
        <StatCard icon={Clock} label="Time changes" value={kpi.teacher_time_change_count ?? 0} testId="kpi-time-changes" />
        <StatCard icon={RefreshCcw} label="Reschedules" value={kpi.teacher_reschedule_count ?? 0} testId="kpi-reschedules" />
        <StatCard icon={AlertTriangle} label="Last-minute" value={kpi.teacher_cancellation_count ?? 0} sub="<24h notice" testId="kpi-cancellations" />
        <StatCard icon={UserX} label="No-shows" value={kpi.no_show_count ?? 0} sub="Highest penalty" testId="kpi-no-shows" />
        <StatCard icon={LifeBuoy} label="Replacements" value={`${kpi.replacement_classes_completed ?? 0}/${kpi.replacement_classes_due ?? 0}`}
          sub={`Completion ${pct(kpi.replacement_completion_rate)}`} testId="kpi-replacements" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
        <div className="rounded-lg border bg-card p-6" data-testid="kpi-dropout-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Dropout statistics (fair ranking)</h3>
            <ConfidenceChip value={drop.ranking_confidence || "none"} testId="kpi-dropout-confidence" />
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><p className="text-xs text-muted-foreground uppercase tracking-wider">Raw dropout rate</p>
              <p className="text-xl font-bold font-display mt-1" data-testid="kpi-raw-dropout">{pct(drop.raw_dropout_rate)}</p></div>
            <div><p className="text-xs text-muted-foreground uppercase tracking-wider">Adjusted rate (fair)</p>
              <p className="text-xl font-bold font-display mt-1" data-testid="kpi-adjusted-dropout">{pct(drop.adjusted_dropout_rate)}</p></div>
            <div><p className="text-xs text-muted-foreground uppercase tracking-wider">Dropouts / served</p>
              <p className="text-xl font-bold font-display mt-1">{drop.dropout_count ?? 0} / {drop.total_students_served ?? 0}</p></div>
            <div><p className="text-xs text-muted-foreground uppercase tracking-wider">Sample size group</p>
              <p className="text-xl font-bold font-display mt-1 capitalize">{drop.sample_size_group || "—"}</p></div>
          </div>
          {drop.warning && (
            <div className="mt-4 flex items-start gap-2 text-xs rounded-md bg-[#FFF8DC] text-[#B8860B] p-3" data-testid="kpi-dropout-warning">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />{drop.warning}
            </div>
          )}
        </div>

        <div className="rounded-lg border bg-card p-6" data-testid="kpi-suggestions-card">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-secondary" />How to improve
          </h3>
          <ul className="space-y-2.5">
            {(kpi.improvement_suggestions || []).map((s, i) => (
              <li key={i} className="text-sm flex items-start gap-2" data-testid={`kpi-suggestion-${i}`}>
                <span className="h-1.5 w-1.5 rounded-full bg-secondary mt-1.5 shrink-0" />{s}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="rounded-lg border bg-card p-6 mt-6" data-testid="kpi-ops-events-card">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h3 className="text-lg font-semibold">Operational events</h3>
          <p className="text-xs text-muted-foreground">
            Penalty {ops.total_penalty ?? 0}{ops.penalty_capped ? ` (capped at ${ops.penalty_cap})` : ""} · Replacement credit +{ops.replacement_credit ?? 0}
          </p>
        </div>
        {!(ops.events || []).length ? <EmptyState text="No operational events — a perfectly reliable record!" /> : (
          <div className="divide-y">
            {ops.events.slice(0, 12).map((e, i) => (
              <div key={i} className="py-2.5 flex items-center gap-3 text-sm" data-testid={`ops-event-${i}`}>
                <span className="font-mono text-xs w-24 shrink-0">{e.date}</span>
                <span className="flex-1 min-w-0">
                  <span className="font-medium capitalize">{String(e.type).replace(/_/g, " ")}</span>
                  <span className="text-muted-foreground"> — {e.detail}</span>
                </span>
                <span className={`font-mono text-xs font-bold ${e.points >= 0 ? "text-[#2D6A4F]" : "text-[#D90429]"}`}>
                  {e.points >= 0 ? `+${e.points}` : e.points}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- /teacher/rewards
export function TeacherRewardsPage() {
  const { data, loading } = useList("/teacher/rewards");
  if (loading) return <Spinner full />;
  if (!data) {
    return (
      <div data-testid="teacher-rewards-page">
        <PageHeader title="Rewards & badges" />
        <EmptyState text="Rewards visibility is currently disabled by the school." />
      </div>
    );
  }
  const earnedCount = (data.badge_catalog || []).filter((b) => b.earned).length;
  return (
    <div data-testid="teacher-rewards-page">
      <PageHeader title="Rewards & badges"
        description="Points are earned from student progress, exam results and reliable teaching — penalties apply for disruptions." />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 stagger-in">
        <StatCard icon={Sparkles} label="Total points" value={`${data.total_points ?? 0} pts`} accent testId="rewards-total-points" />
        <StatCard icon={Medal} label="Badges earned" value={`${earnedCount} / ${(data.badge_catalog || []).length}`} testId="rewards-badge-count" />
        <StatCard icon={ClipboardCheck} label="Reward activities" value={(data.recent_entries || []).length} sub="Recent ledger entries" testId="rewards-entry-count" />
      </div>

      <h3 className="text-xl font-semibold mt-10 mb-4">Badge collection</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 stagger-in" data-testid="badge-grid">
        {(data.badge_catalog || []).map((b) => {
          const Icon = BADGE_ICONS[b.icon] || Award;
          return (
            <div key={b.code}
              data-testid={`badge-${b.code}`}
              className={`rounded-lg border p-5 lift ${b.earned ? "bg-card border-secondary/60" : "bg-muted/40 opacity-70"}`}>
              <div className="flex items-center justify-between mb-3">
                <span className={`h-10 w-10 rounded-full flex items-center justify-center ${b.earned ? "bg-secondary/15 text-secondary" : "bg-muted text-muted-foreground"}`}>
                  <Icon className="h-5 w-5" />
                </span>
                {b.earned
                  ? <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#D8F3DC] text-[#2D6A4F]">Earned</span>
                  : <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
              </div>
              <p className="font-semibold text-sm">{b.name}</p>
              <p className="text-xs text-muted-foreground mt-1">{b.description}</p>
            </div>
          );
        })}
      </div>

      <h3 className="text-xl font-semibold mt-10 mb-4">Recent point activity</h3>
      {!(data.recent_entries || []).length ? <EmptyState text="No reward activity yet — points appear as you teach." /> : (
        <div className="rounded-lg border bg-card divide-y" data-testid="reward-ledger">
          {data.recent_entries.map((e) => (
            <div key={e.id} className="px-4 py-3 flex items-center gap-3 text-sm" data-testid={`ledger-entry-${e.id}`}>
              <span className="font-mono text-xs w-24 shrink-0 text-muted-foreground">{(e.created_at || "").slice(0, 10)}</span>
              <span className="flex-1 min-w-0">
                <span className="font-medium capitalize">{String(e.reason_code).replace(/_/g, " ")}</span>
                {e.description && <span className="text-muted-foreground"> — {e.description}</span>}
              </span>
              <span className={`font-mono text-xs font-bold shrink-0 ${e.points >= 0 ? "text-[#2D6A4F]" : "text-[#D90429]"}`}>
                {e.points >= 0 ? `+${e.points}` : e.points} pts
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- /teacher/ranking
const RANK_COLORS = ["text-[#D4AF37]", "text-[#9CA3AF]", "text-[#B87333]"];

export function TeacherRankingPage() {
  const { data, loading } = useList("/teacher/ranking");
  if (loading) return <Spinner full />;
  if (!data) {
    return (
      <div data-testid="teacher-ranking-page">
        <PageHeader title="My ranking" />
        <EmptyState text="Ranking visibility is currently disabled by the school." />
      </div>
    );
  }
  return (
    <div data-testid="teacher-ranking-page">
      <PageHeader title="My ranking"
        description="An anonymous leaderboard across all teachers — only you can see which row is yours." />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 stagger-in">
        <StatCard icon={Trophy} label="Your rank"
          value={data.your_rank ? `#${data.your_rank} of ${data.total_teachers}` : "Hidden"} accent testId="ranking-your-rank" />
        <StatCard icon={TrendingUp} label="Your KPI score" value={data.your_kpi_score ?? "—"} sub="Out of 100" testId="ranking-your-score" />
        <StatCard icon={Users} label="Teachers ranked" value={data.total_teachers ?? 0} testId="ranking-total" />
      </div>

      <h3 className="text-xl font-semibold mt-10 mb-4">Leaderboard</h3>
      {!(data.leaderboard || []).length ? (
        <EmptyState text="The leaderboard is currently hidden by the school." />
      ) : (
        <div className="space-y-3 stagger-in" data-testid="leaderboard">
          {data.leaderboard.map((r) => (
            <div key={r.rank}
              data-testid={`leaderboard-row-${r.rank}`}
              className={`rounded-lg border p-4 flex flex-wrap items-center gap-4 lift ${r.is_you ? "border-secondary bg-secondary/5" : "bg-card"}`}>
              <span className={`w-10 text-center font-display font-bold text-lg shrink-0 ${RANK_COLORS[r.rank - 1] || "text-muted-foreground"}`}>
                {r.rank <= 3 ? <Trophy className="h-5 w-5 mx-auto" /> : `#${r.rank}`}
              </span>
              <div className="flex-1 min-w-[140px]">
                <p className="font-semibold text-sm flex items-center gap-2">
                  {r.label}
                  {r.is_you && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-secondary/15 text-secondary">You</span>}
                </p>
                <ConfidenceChip value={r.ranking_confidence} />
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">KPI score</p>
                <p className="font-bold font-display">{r.kpi_score}</p>
              </div>
              <div className="text-right hidden sm:block">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Reliability</p>
                <p className="font-bold font-display">{r.operational_quality_score ?? "—"}</p>
              </div>
              <div className="text-right hidden md:block">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Points</p>
                <p className="font-bold font-display">{r.reward_points}</p>
              </div>
              <div className="text-right hidden md:block">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Badges</p>
                <p className="font-bold font-display">{r.badge_count}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      {data.note && (
        <p className="text-xs text-muted-foreground mt-6 flex items-start gap-2" data-testid="ranking-note">
          <ShieldCheck className="h-4 w-4 shrink-0 text-secondary" />{data.note}
        </p>
      )}
    </div>
  );
}
